import { cn } from '@/lib/utils'
import { AdjusterCard } from './AdjusterCard'
import type { ClaimOutcome } from './claimsData'
import { DoneGlyph } from './DoneGlyph'
import { NeedsInputCard } from './NeedsInputCard'
import { OutcomeCard } from './OutcomeCard'
import { StepDetail, StepState, StepTone, type TimelineStep } from './timelineSteps'

const TONE_DOT: Record<StepTone, string> = {
  [StepTone.SUCCESS]: 'border-accent-green bg-accent-green',
  [StepTone.DANGER]: 'border-error bg-error',
  [StepTone.NEUTRAL]: 'border-ink-400 bg-ink-400',
}

const DOT_CLASS: Record<StepState, string> = {
  [StepState.DONE]: 'border-accent-green bg-accent-green',
  [StepState.CURRENT]: 'border-ink-900 bg-surface ring-ink-900/15 ring-3',
  [StepState.PENDING]: 'border-ink-300 bg-surface border-dashed',
}

interface ClaimTimelineStepProps {
  step: TimelineStep
  outcome?: ClaimOutcome
  /** The rail line runs to the next step, so the last one drops it. */
  isLast: boolean
}

export function ClaimTimelineStep({ step, outcome, isLast }: ClaimTimelineStepProps) {
  const done = step.state === StepState.DONE
  const pending = step.state === StepState.PENDING
  const details = step.details ?? []

  return (
    <div className="relative flex gap-3.5 pb-6 last:pb-0">
      <div className="relative flex w-5 shrink-0 flex-col items-center">
        {!isLast && (
          <div className={cn('absolute top-5 -bottom-6 w-0.5', done ? 'bg-accent-green' : 'bg-ink-200')} />
        )}
        <div
          className={cn(
            'z-1 flex size-5 shrink-0 items-center justify-center rounded-full border-2',
            step.tone && done ? TONE_DOT[step.tone] : DOT_CLASS[step.state]
          )}
        >
          {done && <DoneGlyph tone={step.tone} />}
          {step.state === StepState.CURRENT && <span className="bg-ink-900 size-2 rounded-full" />}
        </div>
      </div>

      <div className="min-w-0 flex-1 pt-px">
        <div className="flex items-baseline justify-between gap-2.5">
          <span className={cn('text-sm font-bold', pending && 'text-ink-500 font-semibold')}>
            {step.title}
          </span>
          {step.when && <span className="text-ink-400 shrink-0 font-mono text-xs">{step.when}</span>}
        </div>
        {step.desc && (
          <p className={cn('text-ink-500 mt-0.5 text-sm', pending && 'text-ink-400 italic')}>{step.desc}</p>
        )}
        {details.includes(StepDetail.ADJUSTER) && <AdjusterCard />}
        {details.includes(StepDetail.NEEDS_INPUT) && <NeedsInputCard />}
        {details.includes(StepDetail.OUTCOME) && outcome && <OutcomeCard outcome={outcome} />}
      </div>
    </div>
  )
}
